import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { EMPTY_INSURANCE } from '../../utils/insuranceHelpers';

const WizardContext = createContext(null);

export { WizardContext };

/**
 * Shared state for all wizard steps. All data that crosses step boundaries
 * lives here. Navigation functions (nextStep/previousStep) remain in
 * OnboardingWizardPage since they depend on useNavigate.
 */
const LS_KEY = 'parkpro_wizard_listingId';
const LS_KEY_STEPS = 'parkpro_wizard_completedSteps';
const LS_KEY_ADDRESS = 'parkpro_wizard_addressData';
const LS_KEY_GEOFENCE = 'parkpro_wizard_geofence';
const LS_KEY_INGRESS = 'parkpro_wizard_ingress';
const LS_KEY_EGRESS = 'parkpro_wizard_egress';
const LS_KEY_INSURANCE_VISITED = 'parkpro_wizard_insuranceVisited';

export function WizardProvider({ children, initialHydrated = true, initialInsuranceVisited = false }) {
    const [listingId, setListingIdRaw] = useState(() => {
        try { return localStorage.getItem(LS_KEY) || null; }
        catch { return null; }
    });

    const setListingId = useCallback((id) => {
        setListingIdRaw(id);
        try {
            if (id) localStorage.setItem(LS_KEY, id);
            else    localStorage.removeItem(LS_KEY);
        } catch {}
    }, []);
    const [addressData, setAddressDataRaw] = useState(() => {
        try {
            const stored = localStorage.getItem(LS_KEY_ADDRESS);
            return stored ? JSON.parse(stored) : null;
        } catch { return null; }
    });

    const setAddressData = useCallback((data) => {
        setAddressDataRaw(data);
        try {
            if (data) localStorage.setItem(LS_KEY_ADDRESS, JSON.stringify(data));
            else    localStorage.removeItem(LS_KEY_ADDRESS);
        } catch {}
    }, []);

    const [confirmedFields, setConfirmedFields] = useState({});
    const [photos,          setPhotos]          = useState([]);
    const [geoSpaceCount,   setGeoSpaceCount]   = useState(null);
    const [geofence, setGeofenceRaw] = useState(() => {
        try {
            const stored = localStorage.getItem(LS_KEY_GEOFENCE);
            return stored ? JSON.parse(stored) : null;
        } catch { return null; }
    });

    const setGeofence = useCallback((data) => {
        setGeofenceRaw(data);
        try {
            if (data) localStorage.setItem(LS_KEY_GEOFENCE, JSON.stringify(data));
            else    localStorage.removeItem(LS_KEY_GEOFENCE);
        } catch {}
    }, []);
    const [ingress, setIngressRaw] = useState(() => {
        try {
            const stored = localStorage.getItem(LS_KEY_INGRESS);
            return stored ? JSON.parse(stored) : [];
        } catch { return []; }
    });
    const [egress, setEgressRaw] = useState(() => {
        try {
            const stored = localStorage.getItem(LS_KEY_EGRESS);
            return stored ? JSON.parse(stored) : [];
        } catch { return []; }
    });

    const setIngress = useCallback((data) => {
        const next = Array.isArray(data) ? data : [];
        setIngressRaw(next);
        try {
            if (next.length) localStorage.setItem(LS_KEY_INGRESS, JSON.stringify(next));
            else localStorage.removeItem(LS_KEY_INGRESS);
        } catch {}
    }, []);

    const setEgress = useCallback((data) => {
        const next = Array.isArray(data) ? data : [];
        setEgressRaw(next);
        try {
            if (next.length) localStorage.setItem(LS_KEY_EGRESS, JSON.stringify(next));
            else localStorage.removeItem(LS_KEY_EGRESS);
        } catch {}
    }, []);
    const [pricing,         setPricing]         = useState({ hourlyRate: null, maxSpots: null });
    const [insurance,       setInsurance]       = useState({ ...EMPTY_INSURANCE });
    const [managers,        setManagers]        = useState([]);
    // True when context reflects authoritative server state (or there's
    // nothing to hydrate). Resume mode initializes this to false so child
    // step effects (which fire before the parent hydration effect) see the
    // correct value synchronously at mount, then it flips to true after
    // the GET /listings/:id round-trip completes.
    const [isHydrated,      setIsHydrated]      = useState(initialHydrated);
    // Tracks whether the host has actually landed on Step 6. Without this,
    // the wizard's "No" visual default would alone be enough to mark Step 6
    // complete (since `isInsuranceAddressed({required:false})` is true) —
    // so a host who never reached the page would see Step 6 with a green
    // check in the stepper. Persisted to localStorage and seeded from the
    // resumed listing's `insurance.required !== null` in
    // OnboardingWizardPage so resumes with prior answers stay complete.
    const [hasVisitedInsurance, setHasVisitedInsuranceRaw] = useState(() => {
        if (initialInsuranceVisited) return true;
        try { return localStorage.getItem(LS_KEY_INSURANCE_VISITED) === 'true'; }
        catch { return false; }
    });
    const markInsuranceVisited = useCallback(() => {
        setHasVisitedInsuranceRaw((prev) => {
            if (prev) return prev;
            try { localStorage.setItem(LS_KEY_INSURANCE_VISITED, 'true'); } catch {}
            return true;
        });
    }, []);
    const [completedSteps, setCompletedSteps] = useState(() => {
        try {
            const stored = localStorage.getItem(LS_KEY_STEPS);
            return stored ? JSON.parse(stored) : [];
        } catch { return []; }
    });
    const [maxStepReached, setMaxStepReachedRaw] = useState(1);

    const setMaxStepReached = useCallback((value) => {
        setMaxStepReachedRaw((prev) => {
            const next = typeof value === 'function' ? value(prev) : value;
            return Math.max(prev, next);
        });
    }, []);

    const prevListingIdRef = useRef(listingId);
    useEffect(() => {
        if (prevListingIdRef.current !== listingId) {
            setCompletedSteps([]);
            setMaxStepReachedRaw(1);
            setHasVisitedInsuranceRaw(false);
            try {
                localStorage.removeItem(LS_KEY_STEPS);
                localStorage.removeItem(LS_KEY_INSURANCE_VISITED);
            } catch {}
        }
        prevListingIdRef.current = listingId;
    }, [listingId]);

    /**
     * Mark a step index (1-based) as complete. Idempotent.
     * Called by OnboardingWizardPage's nextStep() before navigating forward.
     */
    const markStepComplete = (stepIndex) =>
        setCompletedSteps(prev => {
            if (prev.includes(stepIndex)) return prev;
            const next = [...prev, stepIndex];
            try { localStorage.setItem(LS_KEY_STEPS, JSON.stringify(next)); } catch {}
            return next;
        });

    const clearFromStep = useCallback((fromStep) => {
        // Keep only steps strictly before fromStep — drops this step's checkmark too.
        setCompletedSteps(prev => {
            const next = prev.filter(s => s < fromStep);
            try { localStorage.setItem(LS_KEY_STEPS, JSON.stringify(next)); } catch {}
            return next;
        });
        if (fromStep <= 2) {
            setGeofence(null);
            setIngress([]);
            setEgress([]);
        }
        if (fromStep <= 3) {
            setConfirmedFields({});
            setGeoSpaceCount(null);
        }
        if (fromStep <= 4) {
            setPhotos([]);
        }
        if (fromStep <= 5) {
            setPricing({ hourlyRate: null, maxSpots: null });
        }
        if (fromStep <= 6) {
            setInsurance({ ...EMPTY_INSURANCE });
            setHasVisitedInsuranceRaw(false);
            try { localStorage.removeItem(LS_KEY_INSURANCE_VISITED); } catch {}
        }
    }, [setGeofence, setIngress, setEgress]);

    const clearPricingStep = useCallback(() => {
        setCompletedSteps(prev => {
            const next = prev.filter(s => s !== 5);
            try { localStorage.setItem(LS_KEY_STEPS, JSON.stringify(next)); } catch {}
            return next;
        });
        setPricing({ hourlyRate: null, maxSpots: null });
    }, []);

    const resetWizard = useCallback(() => {
        setListingIdRaw(null);
        setAddressDataRaw(null);
        setConfirmedFields({});
        setPhotos([]);
        setGeoSpaceCount(null);
        setGeofence(null);
        setIngress([]);
        setEgress([]);
        setPricing({ hourlyRate: null, maxSpots: null });
        setInsurance({ ...EMPTY_INSURANCE });
        setManagers([]);
        setCompletedSteps([]);
        setHasVisitedInsuranceRaw(false);
        try {
            localStorage.removeItem(LS_KEY);
            localStorage.removeItem(LS_KEY_STEPS);
            localStorage.removeItem(LS_KEY_ADDRESS);
            localStorage.removeItem(LS_KEY_GEOFENCE);
            localStorage.removeItem(LS_KEY_INGRESS);
            localStorage.removeItem(LS_KEY_EGRESS);
            localStorage.removeItem(LS_KEY_INSURANCE_VISITED);
        } catch {}
    }, [setGeofence, setIngress, setEgress]);

    const hasAddress = useMemo(() => !!(addressData && (
        addressData.formatted_address
        || addressData.formattedAddress
        || (typeof addressData.lat === 'number' && typeof addressData.lng === 'number')
    )), [addressData]);

    return (
        <WizardContext.Provider value={{
            listingId,       setListingId,
            addressData,     setAddressData,
            hasAddress,
            confirmedFields, setConfirmedFields,
            photos,          setPhotos,
            geoSpaceCount,   setGeoSpaceCount,
            geofence,        setGeofence,
            ingress,         setIngress,
            egress,          setEgress,
            pricing,         setPricing,
            insurance,       setInsurance,
            managers,        setManagers,
            completedSteps,  markStepComplete,
            maxStepReached,  setMaxStepReached,
            isHydrated,      setIsHydrated,
            hasVisitedInsurance, markInsuranceVisited,
            clearFromStep,
            clearPricingStep,
            resetWizard,
        }}>
            {children}
        </WizardContext.Provider>
    );
}

/**
 * Hook for step components to access wizard state.
 * Throws if called outside a WizardProvider (catches mis-use early).
 */
export function useWizard() {
    const ctx = useContext(WizardContext);
    if (!ctx) throw new Error('useWizard must be used within a WizardProvider');
    return ctx;
}

export { isMissingAddressAutosaveError } from '../../utils/listingErrors';
